import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { CatModal } from '@blocks-react/components/Modal';
import { CatInputField } from '@blocks-react/components/InputField';
import { CatToggle } from '@blocks-react/components/Toggle';
import { CatButton } from '@blocks-react/components/Button';
import { CatTextPassage } from '@blocks-react/components/TextPassage';
import WrapperContainer from '@app/components/common/WrapperContainer';
import SystemFeedbackBanner from '@app/components/common/Blocks/SystemFeedbackBanner';
import { createSavedListDetails } from '@app/store/account/actions';
import { MODALS_ROOT } from '@app/constants/targets';
import CreateListFileUpload from './CreateListFileUpload';
import useSavedListUploader from '../hooks/useSavedListUploader';
import {
  PUBLIC_LIST,
  SAVED_LIST_DETAILS_ROW_VALUES,
  AFFILIATION_DEALER,
  DEALER_LIST,
  PRIVATE_LIST,
  BUTTON_VARIANTS
} from '@app/constants/commonConstants';
import {
  CREATE_LIST_MODAL_FIELDS,
  MAXLENGTH,
  SIZE,
  VARIANT
} from '../constants';
import styles from './CreateListModal.module.scss';
import { capitalizeFirstLetter } from '../utils';
import { savedListStatusConst } from '../../saved-list-details/constants';

const CreateListModal = ({ setShowModal, showModal, isCSRPage = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userAffiliation = useSelector(s => s.common?.userAffiliation);
  const isCSPCustomer = useSelector(state => state?.common?.isCSPCustomer);
  const isCSR = useSelector(s => s.common?.isCatCSR) || false;
  const isBuyOnBehalf = useSelector(s => s.common?.isBuyOnBehalf) || false;
  const isDealerUser = userAffiliation === AFFILIATION_DEALER;
  const addProductToSavedListError = useSelector(
    state => state?.account?.addProductToSavedListError
  );
  const createListError = useSelector(state => state?.account?.createListError);
  const isInstantAccess = useSelector(s => s.common?.isInstantAccess);

  const isNotCSRUser = !isCSR && !isBuyOnBehalf;
  const showTypeToggle =
    !isCSRPage &&
    isNotCSRUser &&
    !isDealerUser &&
    (isCSPCustomer || !isInstantAccess);
  const CASE_NUMBER_INPUT_KEY = isCSRPage
    ? 'ENTER_CASE_NUMBER_BOLD'
    : 'ENTER_SAVED_LIST_NAME';
  const CASE_NUMBER_INPUT_ERROR_MESSAGE = isCSRPage
    ? capitalizeFirstLetter(t('ENTER_CASE_NUMBER_BOLD'))
    : t('LIST_NAME_REQUIRED');
  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    setError,
    getValues,
    watch,
    reset,
    formState: { errors, isSubmitSuccessful }
  } = useForm({
    defaultValues: {
      [CREATE_LIST_MODAL_FIELDS.LIST_NAME]: '',
      [CREATE_LIST_MODAL_FIELDS.LIST_TYPE]: false,
      [CREATE_LIST_MODAL_FIELDS.FILE_NAME]: []
    }
  });

  const { handleFileUpload } = useSavedListUploader({
    defaultRowValues: SAVED_LIST_DETAILS_ROW_VALUES
  });
  const isCreateListModalError = createListError || addProductToSavedListError;
  const onCreateListSuccess = () => {
    setShowModal(false);
    reset();
  };
  const onModalClose = () => {
    setShowModal(false);
    reset();
  };
  const onSubmit = values => {
    if (values[CREATE_LIST_MODAL_FIELDS.FILE_NAME][0]?.fileObject) {
      handleFileUpload(
        values[CREATE_LIST_MODAL_FIELDS.FILE_NAME][0].fileObject[1],
        values[CREATE_LIST_MODAL_FIELDS.LIST_NAME],
        onCreateListSuccess
      );
    } else {
      const listType = values[CREATE_LIST_MODAL_FIELDS.LIST_TYPE]
        ? PUBLIC_LIST
        : PRIVATE_LIST;
      const savedListType = isDealerUser ? DEALER_LIST : listType;

      let status = savedListType;

      if (isCSR) {
        status = savedListStatusConst.PUBLIC;
      }

      dispatch(
        createSavedListDetails(
          values[CREATE_LIST_MODAL_FIELDS.LIST_NAME],
          status,
          null,
          null,
          null,
          onCreateListSuccess
        )
      );
    }
  };
  return (
    <WrapperContainer targetId={MODALS_ROOT}>
      <CatModal
        isActive={showModal}
        onBlModalClose={() => onModalClose()}
        heading={t('SL_CREATE_LIST')}
      >
        {isCreateListModalError && isSubmitSuccessful && (
          <div className="pb-3">
            <SystemFeedbackBanner
              heading={t('UNKNOWN_ERROR_MESSAGE')}
              variant="error"
            />
          </div>
        )}
        <div>
          {isCSRPage && (
            <CatTextPassage size="xs">
              <p className="my-1 cat-u-padding-bottom-lg u-color--ExtraDarkGray">
                {t('DELETION_NOTIFICATION_VERBIAGE_FOR_CARTLIST')}
              </p>
            </CatTextPassage>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="cat-u-padding-bottom-xl">
              <CatInputField
                data-testid={CASE_NUMBER_INPUT_KEY}
                {...register(CREATE_LIST_MODAL_FIELDS.LIST_NAME, {
                  required: {
                    value: true,
                    message: CASE_NUMBER_INPUT_ERROR_MESSAGE
                  },
                  validate: value =>
                    !!value.trim() || CASE_NUMBER_INPUT_ERROR_MESSAGE
                })}
                label={t(CASE_NUMBER_INPUT_KEY)}
                maxlength={MAXLENGTH}
                fieldNote={
                  errors?.[CREATE_LIST_MODAL_FIELDS.LIST_NAME]?.message
                }
                status={
                  errors?.[CREATE_LIST_MODAL_FIELDS.LIST_NAME]
                    ? 'error'
                    : undefined
                }
                onBlChange={e => {
                  clearErrors([CREATE_LIST_MODAL_FIELDS.LIST_NAME]);
                  setValue(e.target.name, e.target.value);
                }}
                required
              />
            </div>
            {showTypeToggle && (
              <div
                className={`${styles['list-desc']} u-fs--12 d-flex align-items-center pb-4`}
              >
                <span className="pe-3">
                  {`${t('LISTS_DEFAULT_PRIVATE')} ${t('PUBLIC_TO_SHARE')}`}
                </span>
                <div className="pe-3">
                  <CatToggle
                    data-testid="toggle-list"
                    {...register(CREATE_LIST_MODAL_FIELDS.LIST_TYPE)}
                    variant={VARIANT.PRIMARY}
                    hideLabel
                    size={SIZE.SM}
                    onBlToggleChange={e => {
                      setValue(
                        CREATE_LIST_MODAL_FIELDS.LIST_TYPE,
                        e.target.checked
                      );
                    }}
                    checked={getValues(CREATE_LIST_MODAL_FIELDS.LIST_TYPE)}
                  ></CatToggle>
                </div>
                <span className={`${styles['toggle-desc']}`}>
                  {t('PUBLIC')}
                </span>
              </div>
            )}
            <CreateListFileUpload
              register={register}
              setValue={setValue}
              setError={setError}
              errors={errors}
              watch={watch}
            />
            <CatButton
              type="submit"
              slot="after"
              className="text-nowrap"
              disabled={errors?.[CREATE_LIST_MODAL_FIELDS.LIST_NAME]}
            >
              {t('CREATE_SAVE')}
            </CatButton>
            <CatButton
              className="cat-u-padding-right-sm"
              variant={BUTTON_VARIANTS.GHOST}
              onClick={() => onModalClose()}
            >
              {t('REQUISITIONLIST_BTN_CANCEL')}
            </CatButton>
          </form>
        </div>
      </CatModal>
    </WrapperContainer>
  );
};

CreateListModal.propTypes = {
  setShowModal: PropTypes.func,
  showModal: PropTypes.bool,
  isCSRPage: PropTypes.bool
};

export default CreateListModal;
